/**
 * Cross-Validation Types
 * Types for comparing visa documents against LinkedIn/Twitter data
 */

// Discrepancy severity levels
export type Severity = 'critical' | 'warning' | 'info';

// Types of discrepancies we can detect
export type DiscrepancyType =
  | 'date_mismatch'           // Different start/end dates
  | 'title_mismatch'          // Different job titles
  | 'company_name_variation'  // Inc vs LLC (usually acceptable)
  | 'missing_in_linkedin'     // In visa doc but not on LinkedIn
  | 'missing_in_visa_doc'     // On LinkedIn but not in visa doc
  | 'social_media_mismatch'   // Twitter handle differs
  | 'education_mismatch'      // Education details differ
  | 'employer_mismatch';      // Employer details don't match

// Individual discrepancy
export interface Discrepancy {
  type: DiscrepancyType;
  severity: Severity;
  field: string;           // Which field has the discrepancy
  category: 'employment' | 'education' | 'social_media' | 'personal';

  // The two values being compared
  linkedInValue?: string;
  visaDocValue?: string;
  twitterValue?: string;

  // Context
  companyName?: string;     // For employment discrepancies
  institutionName?: string; // For education discrepancies
  platform?: string;        // For social media discrepancies

  // Analysis
  daysDifference?: number;  // For date mismatches
  similarityScore?: number; // 0-1, how similar the values are

  // Recommendation
  recommendation: string;
}

// Full cross-validation result
export interface CrossValidationResult {
  // Summary counts
  totalDiscrepancies: number;
  criticalCount: number;
  warningCount: number;
  infoCount: number;

  // Discrepancies by category
  employmentDiscrepancies: Discrepancy[];
  educationDiscrepancies: Discrepancy[];
  socialMediaDiscrepancies: Discrepancy[];
  personalDiscrepancies: Discrepancy[];

  // Coverage metrics
  coverageScore: number;           // 0-1, how much data overlapped
  linkedInCoverage: number;        // % of LinkedIn jobs found in visa doc
  visaDocCoverage: number;         // % of visa doc jobs found on LinkedIn

  // Timeline analysis
  timelineGaps: TimelineGap[];     // Gaps in employment history
  overlappingEmployment: OverlappingEmployment[]; // Jobs that overlap

  // Overall recommendations
  recommendations: string[];

  // Timestamps
  analyzedAt: Date;
  documentsCompared: string[];     // Which docs were compared
}

// Timeline gap in employment
export interface TimelineGap {
  fromDate: string;
  toDate: string;
  durationDays: number;
  source: 'linkedin' | 'visa_doc' | 'both';
  recommendation: string;
}

// Overlapping employment periods
export interface OverlappingEmployment {
  job1: {
    company: string;
    title: string;
    startDate: string;
    endDate: string;
    source: 'linkedin' | 'visa_doc';
  };
  job2: {
    company: string;
    title: string;
    startDate: string;
    endDate: string;
    source: 'linkedin' | 'visa_doc';
  };
  overlapDays: number;
  isAcceptable: boolean; // Overlaps < 30 days often acceptable
}

// Employment entry for comparison (normalized format)
export interface NormalizedEmployment {
  company: string;
  companyNormalized: string;  // Lowercase, no Inc/LLC/Corp
  title: string;
  titleNormalized: string;    // Lowercase, no Sr/Lead/Principal variants
  startDate: Date | null;
  endDate: Date | null;
  isCurrent: boolean;
  source: 'linkedin' | 'visa_doc';
  originalData: any;          // Reference to original parsed data
}

// Education entry for comparison (normalized format)
export interface NormalizedEducation {
  institution: string;
  institutionNormalized: string;
  degree: string;
  fieldOfStudy: string;
  startDate: Date | null;
  endDate: Date | null;
  source: 'linkedin' | 'visa_doc';
  originalData: any;
}

// Social media entry for comparison
export interface NormalizedSocialMedia {
  platform: string;
  handle: string;
  handleNormalized: string;  // Lowercase, no @ prefix
  source: 'linkedin' | 'twitter' | 'visa_doc';
}

// Configuration for cross-validation thresholds
export interface CrossValidationConfig {
  // Date tolerance in days
  dateToleranceWarning: number;   // Default: 45 days
  dateToleranceCritical: number;  // Default: 180 days

  // String similarity threshold (0-1)
  companyMatchThreshold: number;  // Default: 0.8
  titleMatchThreshold: number;    // Default: 0.7

  // Employment gap threshold
  maxAcceptableGapDays: number;   // Default: 30

  // What to flag
  flagMissingInLinkedIn: boolean;
  flagMissingInVisaDoc: boolean;
  flagCompanyVariations: boolean;
}

// Default configuration
export const DEFAULT_VALIDATION_CONFIG: CrossValidationConfig = {
  dateToleranceWarning: 45,
  dateToleranceCritical: 180,
  companyMatchThreshold: 0.8,
  titleMatchThreshold: 0.7,
  maxAcceptableGapDays: 30,
  flagMissingInLinkedIn: true,
  flagMissingInVisaDoc: true,
  flagCompanyVariations: false, // Usually just info, not a real issue
};

// Helper to get severity color for UI
export function getSeverityColor(severity: Severity): string {
  switch (severity) {
    case 'critical':
      return '#ef4444'; // Red
    case 'warning':
      return '#f97316'; // Orange
    case 'info':
      return '#22c55e'; // Green
  }
}

// Helper to get severity label
export function getSeverityLabel(severity: Severity): string {
  switch (severity) {
    case 'critical':
      return 'Critical';
    case 'warning':
      return 'Warning';
    case 'info':
      return 'Info';
  }
}
